import React, { useEffect } from "react";
import { observer } from "mobx-react";
import copilotStore from "Stores/copilotStore";
import SparkleIcon from "Components/common/SparkleIcon";

/*
 * What the drawer shows before a conversation exists. The thread area is empty
 * at that point, and an empty panel reads as broken rather than ready -- so it
 * offers a way in: things worth asking about THIS page, and the last chat to
 * pick back up.
 */

// Openers worth suggesting per section. Page-specific on purpose: the generic
// prompt bank teaches the product, but someone standing on Inbox wants inbox
// questions, not "find fintech companies".
const STARTERS = {
  sequences: [
    "Which sequence is performing best?",
    "What should I fix first?",
    "Which sequences are paused or draft?"
  ],
  sequences_detail: [
    "How is this sequence doing?",
    "Which step is losing people?",
    "Rewrite the first email"
  ],
  contacts: [
    "Who should I follow up with?",
    "Which contacts have no email?",
    "Summarise this list"
  ],
  contacts_detail: [
    "What do I know about them?",
    "Draft an opener",
    "What is the next step?"
  ],
  inbox: [
    "What needs a reply first?",
    "Any meeting requests?",
    "Draft a reply to the open thread"
  ],
  tasks: ["What should I do first?", "Why are these overdue?", "Batch my calls"],
  calls: ["How are my calls going?", "When do people pick up?", "Prep my next call"],
  reports: ["What changed this month?", "Where am I losing deals?", "Explain this drop"],
  accounts: ["Which accounts are worth working?", "Who has no contacts yet?"],
  dashboard: ["How am I doing this week?", "What needs attention today?"],
  settings: ["Is my setup healthy?", "What is missing?"],
  templates: ["Which template gets replies?", "Write me a new one"],
  deep_research: ["Turn this into an opener", "Build a sequence from this"]
};

const DEFAULT_STARTERS = [
  "What can you do here?",
  "What needs my attention?",
  "Summarise this page"
];

const DrawerStartup = observer(({ surface }) => {
  // The recent list is what makes "pick up where you left off" possible, and
  // the drawer may never have opened its history panel.
  useEffect(() => {
    if (!copilotStore.conversations.length) copilotStore.loadConversations();
  }, []);

  const starters = (STARTERS[surface] || DEFAULT_STARTERS).slice(0, 3);
  const recent = copilotStore.conversations.length ? copilotStore.conversations[0] : null;

  return (
    <div className="cdStartup">
      <SparkleIcon className="cdStartup__mark" />
      <div className="cdStartup__title">What&apos;s on your mind?</div>
      <div className="cdStartup__sub">
        I can read this page, answer questions about it, and act on it for you.
      </div>

      <div className="cdStartup__label">Try asking</div>
      <div className="cdStartup__chips">
        {starters.map(text => (
          <button
            type="button"
            key={text}
            className="cdStartup__chip"
            onClick={() => copilotStore.sendUserMessage(text)}
          >
            {text}
          </button>
        ))}
      </div>

      {recent && (
        <>
          <div className="cdStartup__label">Pick up where you left off</div>
          <button
            type="button"
            className="cdStartup__resume"
            onClick={() => copilotStore.loadConversation(recent.id)}
          >
            <i className="material-icons-outlined">history</i>
            <span>{recent.title || "Last conversation"}</span>
          </button>
        </>
      )}
    </div>
  );
});

export default DrawerStartup;
